import api from "@/services/api";

// ─── Private helpers ──────────────────────────────────────────────────────────

/** Returns today as YYYY-MM-DD */
const todayISO = () => new Date().toISOString().split("T")[0];

/** Adds N years to a YYYY-MM-DD string */
const addYears = (dateStr, n) => {
    const d = new Date(dateStr);
    d.setFullYear(d.getFullYear() + n);
    return d.toISOString().split("T")[0];
};

/** Adds N years then subtracts one day (inclusive yearly range). */
const addYearsMinusOneDay = (dateStr, n) => {
    const d = new Date(dateStr);
    d.setFullYear(d.getFullYear() + n);
    d.setDate(d.getDate() - 1);
    return d.toISOString().split("T")[0];
};

/**
 * Returns 1 (minor) if the person is under 18, otherwise 0.
 * Used for the `is_minor` dependent field.
 */
const isMinorFn = (dob) => {
    if (!dob) return 0;
    const birth = new Date(dob);
    const now = new Date();
    const age =
        now.getFullYear() -
        birth.getFullYear() -
        (now <
            new Date(now.getFullYear(), birth.getMonth(), birth.getDate())
            ? 1
            : 0);
    return age < 18 ? 1 : 0;
};

/**
 * Derives the API "variant" string from the active members in Redux.
 *
 * Variant options:
 *   couple | couple_child | couple_2Child | couple_3child
 *   single_parent_Child | single_parent_2Child | single_parent_3child | singleparent_4child
 *   null  → Personal Shield / no combination
 */
const deriveVariant = (members) => {
    const { spouse, children, father, mother } = members;
    const hasSpouse = spouse?.isActive && !!spouse.dob;
    const activeChildren = (children ?? []).filter((c) => !!c.dob);
    const childCount = activeChildren.length;
    const hasParent =
        (father?.isActive && !!father.dob) ||
        (mother?.isActive && !!mother.dob);

    if (hasSpouse) {
        if (childCount === 0) return "couple";
        if (childCount === 1) return "couple_child";
        if (childCount === 2) return "couple_2Child";
        if (childCount >= 3) return "couple_3child";
    }

    if (!hasSpouse && hasParent) {
        if (childCount === 1) return "single_parent_Child";
        if (childCount === 2) return "single_parent_2Child";
        if (childCount === 3) return "single_parent_3child";
        if (childCount >= 4) return "singleparent_4child";
    }

    return null;
};

/**
 * Redux stores images as { name, base64, type } or string URLs (from restored drafts).
 * The API expects the raw base64 string or URL path (or null).
 */
const extractBase64 = (imageObj) => {
    if (typeof imageObj === "string") return imageObj;
    return imageObj?.base64 ?? null;
};

/** Converts known relationship values to the title-cased format expected by draft API. */
const normalizeRelationshipForDraft = (relation) => {
    const value = (relation || "").trim().toLowerCase();
    const map = {
        wife: "Spouse",
        husband: "Spouse",
        spouse: "Spouse",
        son: "Son",
        daughter: "Daughter",
        father: "Father",
        mother: "Mother",
    };

    if (map[value]) return map[value];
    if (!value) return "";
    return value.charAt(0).toUpperCase() + value.slice(1);
};

// ─── Payload builder ──────────────────────────────────────────────────────────

/**
 * Assembles the full API payload from Redux state + auth user.
 *
 * @param {object} plan     – state.mukamalSehat.plan
 * @param {object} members  – state.mukamalSehat.members
 * @param {object} payment  – state.mukamalSehat.payment
 * @param {object} user     – session.user (from Redux auth slice)
 * @returns {object}  Ready-to-POST payload
 */
export const buildSehatPayload = ({ plan, members, payment, user }) => {
    const self = members.self ?? {};
    const startDate = todayISO();
    const endDate = addYears(startDate, 1);

    // Final premium after any coin discount
    const premiumPaid = payment.isRedeemed
        ? Math.max(0, (plan.totalPremium ?? 0) - (payment.coinDiscount ?? 0))
        : (plan.totalPremium ?? 0);

    // Donation = 1% of premium, truncated to 2 dp
    const donationAmount = Math.trunc(premiumPaid * 0.01 * 100) / 100;

    // Coins used: 1 PKR = 0.1 coins (same formula used across other products)
    const usedCoins = payment.isRedeemed
        ? Math.round((payment.coinDiscount ?? 0) / 0.1)
        : 0;

    // ── Dependents ────────────────────────────────────────────────────────────
    const dependents = [];

    const pushDependent = (member) => {
        if (!member?.dob) return; // skip if no DOB (wasn't added in pricing step)
        const minor = isMinorFn(member.dob);
        dependents.push({
            full_name: member.fullName || "",
            relationship: (member.relation || "").toLowerCase(), // API expects lowercase
            date_of_birth: member.dob,
            marital_status: member.maritalStatus || null,
            is_minor: minor,                  // 0 | 1
            cnic: member.cnic || null,
            // Adults carry CNIC images; minors carry B-Form
            cnic_front_image: minor ? null : extractBase64(member.cnicFrontImage),
            cnic_back_image: minor ? null : extractBase64(member.cnicBackImage),
            b_form_front_image: minor ? extractBase64(member.bFormImage) : null,
            individual_premium: member.premium ?? 0,
        });
    };

    // Order: spouse → father → mother → children (matches UI order)
    if (members.spouse?.isActive) pushDependent(members.spouse);
    if (members.father?.isActive) pushDependent(members.father);
    if (members.mother?.isActive) pushDependent(members.mother);
    (members.children ?? []).forEach((child) => pushDependent(child));
    window.console.log("Building payload with dependents:", dependents);
    // ── Payload ───────────────────────────────────────────────────────────────
    return {
        policy_type: "cashless",
        goal_name: "Insurance",
        agent_code: "self",
        goal_amount_sa: 1,
        rate: 0,

        first_contribution: premiumPaid,
        cover_amount: plan.annualCoverage ?? 0,
        premium_amount: premiumPaid,

        start_date: startDate,
        end_date: endDate,
        payment_frequency: "yearly",

        // Policy holder demographics (auth user is source of truth)
        gender: user?.gender || "",
        date_of_birth: self.dob || user?.date_of_birth || "",
        city: (self.city || user?.city || "").toLowerCase(),
        cnic_issue_date: self.cnicIssuanceDate || "",
        cnic_expiry_date: self.cnicExpiryDate || "",
        cnic_front_image: extractBase64(self.cnicFrontImage),
        cnic_back_image: extractBase64(self.cnicBackImage),

        used_coins: usedCoins,
        donation_center: payment.selectedNgo || "",
        donation_amount: donationAmount,

        // Redirect URL after payment gateway processing
        // Parameters let the receiving page restore the summary screen state.
        user_callback_url: (() => {
            const baseUrl = import.meta.env.VITE_FRONTEND_URL + "/signup-before-pricing/";
            const params = new URLSearchParams({
                source: "cashless",
                step: 4,
                "package-title": plan.packageTitle || "",
                total: premiumPaid,
                contribution: premiumPaid,
                donated: donationAmount,
            });
            return `${baseUrl}?${params.toString()}`;
        })(),

        // Policy holder identity
        full_name: user?.full_name || self.fullName || "",
        mobile_number: user?.msisdn || "",
        email_address: self.email || user?.email || "",
        marital_status: self.maritalStatus || user?.marital_status || "",
        cnic: self.cnic || user?.cnic || "",
        residential_address: self.residentialAddress || "",

        // Plan details
        plan: plan.planType === "Family" ? "family_shield" : "personal_shield",
        variant: deriveVariant(members),
        package: plan.packageTitle || "",
        annual_coverage: plan.annualCoverage ?? 0,

        ledger_entries: {
            transaction_type: "credit",
            transaction_purpose: "first_contribution",
            amount: premiumPaid,
            discount_amount: payment.coinDiscount ?? 0,
            payment_method: "jazzcash",
        },

        dependents,

        // Each add-on selected by the user in SehatPricingQuote is stored as
        // { key, title, price } in plan.addOns. Map to the rider contract.
        // rider_code and rider_type are derived from the known add-on keys.
        subscription_riders: (plan.addOns ?? []).map((addon) => ({
            rider_code: addon.rider_code ?? "",
            rider_type: addon.rider_type ?? "",
            sum_assured_percent: 0.00,
            sum_assured_value: 0.00,
            premium_amount: addon.price ?? 0,
        })),
    };
};

/**
 * Builds create-draft payload for Mukammal Sehat using the schema required by /policy/draft.
 */
export const buildSehatDraftPayload = ({ plan, members, payment, user, draftId, subscription_id }) => {
    const self = members?.self ?? {};
    const startDate = todayISO();
    const endDate = addYearsMinusOneDay(startDate, 1);

    const premiumPaid = payment?.isRedeemed
        ? Math.max(0, (plan?.totalPremium ?? 0) - (payment?.coinDiscount ?? 0))
        : (plan?.totalPremium ?? 0);

    const dependents = [];

    const pushDependent = (member) => {
        if (!member?.dob) return;
        const minor = isMinorFn(member.dob);
        dependents.push({
            full_name: member.fullName || "",
            relationship: normalizeRelationshipForDraft(member.relation),
            date_of_birth: member.dob,
            marital_status: member.maritalStatus || null,
            is_minor: minor,
            cnic: minor ? null : (member.cnic || null),
            cnic_front_image: minor ? null : extractBase64(member.cnicFrontImage),
            cnic_back_image: minor ? null : extractBase64(member.cnicBackImage),
            b_form_front_image: minor ? extractBase64(member.bFormImage) : null,
            individual_premium: member.premium ?? 0,
        });
    };

    if (members?.spouse?.isActive) pushDependent(members.spouse);
    if (members?.father?.isActive) pushDependent(members.father);
    if (members?.mother?.isActive) pushDependent(members.mother);
    (members?.children ?? []).forEach((child) => pushDependent(child));

    const planType = plan?.planType === "Family" ? "Family" : "Personal";

    return {
        subscription_id: draftId || subscription_id || null,
        customer_id: user?.customer_id || user?.id || null,
        policy_type: "cashless",
        premium_amount: premiumPaid,
        start_date: startDate,
        end_date: endDate,
        payment_frequency: "yearly",
        rate: null,
        label: `Mukammal Sehat - ${planType} Plan`,

        residential_address: self.residentialAddress || "",
        plan: "Mukammal Sehat",
        variant: planType,
        package: plan?.packageTitle || "",
        annual_coverage: plan?.annualCoverage ?? 0,

        cnic_front_image: extractBase64(self.cnicFrontImage) || extractBase64(user?.cnic_front_image),
        cnic_back_image: extractBase64(self.cnicBackImage) || extractBase64(user?.cnic_back_image),

        // Explicit self (primary member) object for draft persistence
        self: {
            dob: self.dob || user?.date_of_birth || "",
            premium: self.premium ?? 0,
            full_name: self.fullName || user?.full_name || "",
            email: self.email || user?.email || "",
            cnic: self.cnic || user?.cnic || "",
            city: (self.city || user?.city || "").toUpperCase(),
            marital_status: self.maritalStatus || user?.marital_status || "",
            cnic_issuance_date: self.cnicIssuanceDate || "",
            cnic_expiry_date: self.cnicExpiryDate || "",
            residential_address: self.residentialAddress || "",
            cnic_front_image: extractBase64(self.cnicFrontImage) || extractBase64(user?.cnic_front_image),
            cnic_back_image: extractBase64(self.cnicBackImage) || extractBase64(user?.cnic_back_image),
        },

        dependents,
        beneficiaries: [],
        subscription_riders: (plan?.addOns ?? []).map((addon) => ({
            rider_code: addon.rider_code ?? "",
            rider_type: addon.rider_type ?? "",
            sum_assured_percent: 0,
            sum_assured_value: 0,
            premium_amount: addon.price ?? 0,
        })),
    };
};

// ─── Service ──────────────────────────────────────────────────────────────────

export const sehatService = {
    /**
     * Submits the Mukamal Sehat policy application to the backend.
     * @param {object} payload – output of buildSehatPayload()
     * @returns {Promise<object>} API response data
     */
    submitPolicy: async (payload) => {
        const response = await api.post(
            "/policy/premature/subscription",
            payload
        );
        return response.data;
    },
};
